/**
 * adminActions.ts — de destructieve beheerdersacties, nu richting server.
 *
 * WAT HIER MIS WAS
 *
 * "Verwijderen", "Voortgang resetten" en "Nieuwe code" waren alle drie pure
 * mutaties op de lokale store, terwijl de bevestiging beloofde dat het niet
 * ongedaan te maken was. Drie verschillende manieren waarop dat misgaat:
 *
 *   VERWIJDEREN  verdween van dit apparaat en bleef op de server staan. Een
 *                beheerder kon dus denken dat hij een verwijderverzoek had
 *                afgehandeld terwijl er niets was gewist.
 *   RESETTEN     wiste de lokale voortgang, waarna de eerstvolgende
 *                synchronisatie hem gewoon terugzette. Het herstelde zichzelf
 *                buiten het zicht van degene die het deed.
 *   NIEUWE CODE  verzon een code in de browser. De server hield de oude, dus
 *                de beheerder las een code voor die nergens bij hoorde.
 *
 * DE VOLGORDE
 *
 * Alle drie wachten nu eerst op de server en passen de lokale kopie pas aan
 * als die slaagt. Andersom toont het scherm "gelukt" terwijl de server het
 * tegendeel vindt, en dat komt pas de volgende sessie uit — als de gegevens
 * onverklaarbaar terug zijn.
 */

import { supabase } from './client';
import { deleteLearnerData } from './dataRights';
import { error as logError } from '../../utils/logger';
export interface AdminActionResult {
  ok: boolean;
  message?: string;
  /** Wat er per tabel is opgeruimd. Verantwoordingsplicht, art. 5 lid 2. */
  deleted?: Record<string, number>;
  /** Bij een nieuwe koppelcode: de code zoals de server hem nu kent. */
  code?: string;
}

/**
 * Wist de voortgang van een leerling op de server.
 *
 * Het profiel en de inlog blijven bestaan — dit is "begin opnieuw", niet
 * "verdwijn". Die twee scheiden is belangrijk: een beheerder die een vastgelopen
 * leerlijn wil herstellen, wil het kind niet zijn account afnemen.
 */
export async function adminResetProgress(learnerId: string): Promise<AdminActionResult> {
  const {
    data,
    error
  } = await supabase.rpc('admin_reset_learner_progress', {
    p_learner_id: learnerId
  });
  if (error) {
    logError('[AdminActions] Reset mislukt', error);
    return {
      ok: false,
      message: describeRpcError(error.message, 'De voortgang is niet gereset. Er is niets gewijzigd.')
    };
  }
  return {
    ok: true,
    deleted: (data ?? {}) as Record<string, number>
  };
}

/**
 * Verwijdert een leerlingaccount inclusief alle gegevens.
 *
 * Loopt bewust via `delete_learner_data(..., true)` en niet via een eigen
 * admin-variant. Die functie kent élke tabel waar gegevens van een leerling in
 * kunnen staan en wordt bij elke schemawijziging bijgewerkt; een tweede
 * verwijderroute zou daar onvermijdelijk achteraan gaan lopen — en dan wist de
 * ene knop meer dan de andere zonder dat iemand het merkt.
 */
export async function adminDeleteLearner(learnerId: string): Promise<AdminActionResult> {
  const res = await deleteLearnerData(learnerId, true);
  return {
    ok: res.ok,
    message: res.message,
    deleted: res.deleted
  };
}

/**
 * Vervangt de koppelcode van een leerling.
 *
 * Geeft de code terug zoals de SERVER hem nu kent, niet zoals de client hem
 * zou verzinnen. Dat verschil was precies de bug: de beheerder las een code
 * voor die bij geen enkel profiel hoorde.
 */
export async function adminRegenerateLinkCode(learnerId: string): Promise<AdminActionResult> {
  const {
    data,
    error
  } = await supabase.rpc('admin_regenerate_link_code', {
    p_learner_id: learnerId
  });
  if (error) {
    logError('[AdminActions] Nieuwe koppelcode mislukt', error);
    return {
      ok: false,
      message: describeRpcError(error.message, 'De koppelcode is niet vervangen. De oude code werkt nog.')
    };
  }
  return {
    ok: true,
    code: String(data)
  };
}

/**
 * Vertaalt een databasefout naar iets bruikbaars.
 *
 * De enige fout die een beheerder zelf kan oplossen is "geen venster open" —
 * die verdient dus een eigen tekst met de handeling erin. Al het andere is een
 * storing, en dan is de ruwe Postgres-melding voor niemand nuttig: die noemt
 * interne functienamen en zegt niets over wat er nu te doen valt.
 */
function describeRpcError(raw: string | undefined, fallback: string): string {
  if (raw && raw.includes('toegangsvenster')) {
    return 'Open eerst een toegangsvenster voor deze leerling. Deze handeling wordt vastgelegd.';
  }
  return fallback;
}